import { createContext, useContext } from 'react';
import type { SlateContent } from '../types';
import type { FieldsState, ActionType } from './fieldsReducer';
import type { OnSubmitParams } from './types';

/**
 * Public context surface for the reporter fields provider: the context objects, their value types,
 * and the consumer hooks. Kept separate from `Fields.Context.tsx` (the provider component) so
 * read-only consumers don't depend on the provider's full module graph. The three contexts are split
 * (State / Dispatch / Countdown) so dispatch-only and countdown-only consumers don't re-render on
 * state changes.
 */
export type FieldsCountdownContextProps = {
  countdownSeconds: number | null;
};

export type FieldsDispatch = {
  dispatch: (action: ActionType) => void;
  handleInsertTemplate: () => void;
  onReportChange: (content: SlateContent) => void;
  onAddendumChange: (content: SlateContent) => void;
  handleSubmit: (options?: OnSubmitParams) => Promise<void>;
  handleCancelSubmit: () => Promise<void>;
  handleAddendumAdd: () => Promise<void>;
  handleCancelAddAddendum: (options?: OnSubmitParams) => Promise<void>;
  handleSaveDraft: (options?: OnSubmitParams) => Promise<void>;
};

export const FieldsStateContext = createContext<Nullable<FieldsState>>(undefined);
export const FieldsCountdownContext =
  createContext<Nullable<FieldsCountdownContextProps>>(undefined);
export const FieldsDispatchContext = createContext<Nullable<FieldsDispatch>>(undefined);

export const useFieldsState = (): FieldsState => {
  const fieldsContext = useContext(FieldsStateContext);

  if (!fieldsContext) {
    throw new Error('useFieldsState must be used within a FieldsProvider.');
  }

  return fieldsContext;
};

export const useFieldsCountdown = (): FieldsCountdownContextProps => {
  const fieldsContext = useContext(FieldsCountdownContext);

  if (!fieldsContext) {
    throw new Error('useFieldsCountdown must be used within a FieldsProvider.');
  }

  return fieldsContext;
};

export const useFieldsDispatch = (): FieldsDispatch => {
  const fieldsDispatch = useContext(FieldsDispatchContext);

  if (!fieldsDispatch) {
    throw new Error('useFieldsDispatch must be used within a FieldsProvider.');
  }

  return fieldsDispatch;
};

export const useFields = (): [FieldsState, FieldsDispatch] => {
  return [useFieldsState(), useFieldsDispatch()];
};
